import { useTranslation } from "react-i18next";
import { Lock, Pencil } from "lucide-react";
import { Field, SectionTitle } from "./fields";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/Select";
import type { CustomerStatus } from "../types";

const STATUS_OPTIONS: CustomerStatus[] = ["active", "inactive", "new"];

// Read-only view keeps the series + last digits of the passport and the last 4 of the PINFL.
const PASSPORT_MASK: [number, number] = [2, 2];
const PIN_MASK: [number, number] = [0, 4];

interface IdentityFormProps {
  form: Record<string, string>;
  changed: Set<string>;
  setField: (f: string, v: string) => void;
  status: CustomerStatus;
  setStatus: (s: CustomerStatus) => void;
  // Clears the parent save error on any edit (status select handles it inline here).
  onClearError: () => void;
  // false = locked read-only view with passport / PINFL masked (task 0153).
  editing: boolean;
  onEdit: () => void;
}

// Personal / Document / Address field grid + status select. Owns no state — the
// parent (index.tsx) holds form/status and diffs them. Extracted in task 0067.02.
export function IdentityForm({
  form,
  changed,
  setField,
  status,
  setStatus,
  onClearError,
  editing,
  onEdit,
}: IdentityFormProps) {
  const { t } = useTranslation();
  const lock = { editing };
  return (
    <>
      {editing ? (
        <div className="mb-5 flex items-center gap-3 rounded-xl border border-green/25 bg-green-bg-soft px-3.5 py-3">
          <Pencil size={16} strokeWidth={2} className="flex-none text-green-dark" aria-hidden />
          <p className="text-[13px] font-medium text-green-deep">{t("drawer.editingHint")}</p>
        </div>
      ) : (
        <div className="mb-5 flex items-center gap-3 rounded-xl border border-grey-200 bg-grey-25 px-3.5 py-2.5">
          <Lock size={16} strokeWidth={2} className="flex-none text-grey-400" aria-hidden />
          <p className="flex-1 text-[13px] text-grey-600">{t("drawer.lockedHint")}</p>
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex h-9 flex-none items-center gap-1.5 rounded-control border border-grey-300 bg-white px-3 text-[13px] font-semibold text-grey-800 transition hover:border-green hover:text-green-dark active:scale-[0.98]"
          >
            <Pencil size={14} strokeWidth={2} aria-hidden />
            {t("drawer.edit")}
          </button>
        </div>
      )}

      <SectionTitle>{t("drawer.sectionPersonal")}</SectionTitle>
      {/* Column gap only: each Field carries its own mb-4, so a row gap would double the
          spacing once the pairs stack into one column on phones. */}
      <div className="grid grid-cols-1 gap-x-3.5 sm:grid-cols-2">
        <Field {...lock} label={t("drawer.firstName")} value={form.first_name} changed={changed.has("first_name")} onChange={(v) => setField("first_name", v)} />
        <Field {...lock} label={t("drawer.lastName")} value={form.last_name} changed={changed.has("last_name")} onChange={(v) => setField("last_name", v)} />
      </div>
      <div className="grid grid-cols-1 gap-x-3.5 sm:grid-cols-2">
        <Field {...lock} label={t("drawer.phone")} value={form.phone_number} changed={changed.has("phone_number")} onChange={(v) => setField("phone_number", v)} />
        <Field {...lock} label={t("drawer.birthDate")} value={form.birth_date} type="date" changed={changed.has("birth_date")} onChange={(v) => setField("birth_date", v)} />
      </div>

      <SectionTitle className="mt-2">{t("drawer.sectionDocument")}</SectionTitle>
      <div className="grid grid-cols-1 gap-x-3.5 sm:grid-cols-2">
        <Field {...lock} mask={PASSPORT_MASK} label={t("drawer.passport")} value={form.passport_number} changed={changed.has("passport_number")} onChange={(v) => setField("passport_number", v)} />
        <Field {...lock} mask={PIN_MASK} label={t("drawer.pin")} value={form.pin} changed={changed.has("pin")} onChange={(v) => setField("pin", v)} />
      </div>

      <SectionTitle className="mt-2">{t("drawer.sectionAddress")}</SectionTitle>
      <Field {...lock} label={t("drawer.address")} value={form.address} changed={changed.has("address")} onChange={(v) => setField("address", v)} />

      <div className="mb-4 flex flex-col gap-[7px]">
        <label
          htmlFor="customer-status"
          className={`text-[13px] font-semibold ${editing ? "text-grey-700" : "text-grey-500"}`}
        >{t("drawer.status")}</label>
        {/* No empty option — STATUS_OPTIONS always has a valid value; SELECT_NONE not needed.
            `changed.has("status")` drives amber warning border (unsaved), not a validation error. */}
        <Select
          value={status}
          disabled={!editing}
          onValueChange={(v) => {
            setStatus(v as CustomerStatus);
            onClearError();
          }}
        >
          <SelectTrigger
            id="customer-status"
            className={
              !editing
                ? "cursor-default border-grey-100 bg-grey-25 disabled:cursor-default disabled:opacity-100"
                : changed.has("status")
                  ? "border-warning bg-warning-bg hover:border-warning"
                  : undefined
            }
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s}>
                {t(`status.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );
}
